import {
  CameraIcon,
  ChevronLeftIcon,
  EmojiHappyIcon,
  MenuIcon,
  MicrophoneIcon,
  PhoneIcon,
  PhotographIcon,
  PlusIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import type { ReactNode } from 'react';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import PhoneFrame from './PhoneFrame';
import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay } from '../../primitives';

export type LineLine =
  | { from: 'customer'; text: ReactNode; at: number; time: string }
  | {
      from: 'agent';
      text: ReactNode;
      at: number;
      time: string;
      typingAt?: number;
    };

/**
 * A customer's chat with a business's LINE Official Account, in LINE's own
 * look: the blue-grey background, green bubbles for the customer, and the
 * "Read" mark and time beside each bubble rather than inside it.
 */
export default function LineChat({
  brand,
  lines,
}: {
  brand: DemoBrand;
  lines: LineLine[];
}) {
  const { t } = useChannelsT();

  return (
    <PhoneFrame bar='bg-white text-[#111] dark:bg-[#1f2329] dark:text-white'>
      <div className='flex items-center gap-2 bg-white px-3 pb-2.5 pt-1 text-[#111] dark:bg-[#1f2329] dark:text-white'>
        <ChevronLeftIcon className='h-5 w-5 flex-none rtl:-scale-x-100' />
        <span className='min-w-0 flex-1 truncate text-[15px] font-semibold'>
          {brand.name}
        </span>
        <SearchIcon className='h-[18px] w-[18px] flex-none' />
        <PhoneIcon className='h-[18px] w-[18px] flex-none' />
        <MenuIcon className='h-[18px] w-[18px] flex-none' />
      </div>

      <div className='flex min-h-[25rem] flex-col justify-end gap-2.5 bg-[#93abc9] px-2.5 pb-3 pt-3 text-[13.5px] leading-[1.4] text-[#111] dark:bg-[#2a3038] dark:text-[#eef1f5]'>
        <p
          className={clsxm(
            styles.enter,
            'mx-auto rounded-full bg-black/15 px-2.5 py-0.5 text-[11px] font-medium text-white dark:bg-white/10',
          )}
          style={delay(150)}
        >
          {t('Today')}
        </p>

        {lines.map((line, index) =>
          line.from === 'customer' ? (
            <div
              key={index}
              className={clsxm(
                styles.bubble,
                styles.bubbleEnd,
                'flex items-end justify-end gap-1.5',
              )}
              style={delay(line.at)}
            >
              <span className='flex-none pb-0.5 text-end text-[10px] leading-tight text-[#3d4e63] dark:text-[#9aa5b3]'>
                <span className='block'>{t('Read')}</span>
                <span dir='ltr' className='block tabular-nums'>
                  {line.time}
                </span>
              </span>
              <p className='max-w-[74%] rounded-[18px] bg-[#86d97b] px-3 py-2 dark:bg-[#3e8f4a] dark:text-white'>
                {line.text}
              </p>
            </div>
          ) : (
            <div key={index} className='grid'>
              {line.typingAt !== undefined && (
                <span
                  className={clsxm(
                    styles.typing,
                    'ms-10 flex h-8 w-14 items-center justify-center gap-1 self-start rounded-[18px] bg-white [grid-area:1/1] dark:bg-[#3a414a]',
                  )}
                  style={delay(line.typingAt, {
                    '--len': `${line.at - line.typingAt}ms`,
                  })}
                >
                  <span className='h-1.5 w-1.5 rounded-full bg-[#9aa3ad]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#9aa3ad]' />
                  <span className='h-1.5 w-1.5 rounded-full bg-[#9aa3ad]' />
                </span>
              )}
              <div
                className={clsxm(
                  styles.bubble,
                  styles.bubbleStart,
                  'flex items-start gap-2 [grid-area:1/1]',
                )}
                style={delay(line.at)}
              >
                <span
                  className={clsxm(
                    'mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full text-[13px] font-semibold text-white',
                    brand.bg,
                  )}
                >
                  {brand.initial}
                </span>
                <span className='flex min-w-0 items-end gap-1.5'>
                  <p className='max-w-[13.5rem] rounded-[18px] bg-white px-3 py-2 dark:bg-[#3a414a]'>
                    {line.text}
                  </p>
                  <span
                    dir='ltr'
                    className='flex-none pb-0.5 text-[10px] tabular-nums text-[#3d4e63] dark:text-[#9aa5b3]'
                  >
                    {line.time}
                  </span>
                </span>
              </div>
            </div>
          ),
        )}
      </div>

      <div className='flex items-center gap-2.5 bg-white px-3 pb-4 pt-2 text-[#5b6470] dark:bg-[#1f2329] dark:text-[#aab2bd]'>
        <PlusIcon className='h-5 w-5 flex-none' />
        <CameraIcon className='h-5 w-5 flex-none' />
        <PhotographIcon className='h-5 w-5 flex-none' />
        <span className='flex h-9 min-w-0 flex-1 items-center justify-between rounded-full bg-[#f2f3f5] px-3 text-[14px] text-[#9aa3ad] dark:bg-[#2c323a]'>
          Aa
          <EmojiHappyIcon className='h-5 w-5 flex-none' />
        </span>
        <MicrophoneIcon className='h-5 w-5 flex-none' />
      </div>
    </PhoneFrame>
  );
}
